<template>
  <div v-if="!loading" class="donation-request-summary" redesign>
    <!-- Header -->
    <div class="local-hero local-hero-dark no-padding">
      <div class="local-hero-graphic">
        <div class="local-hero-header-wrapper">
          <h1 class="local-hero-header">{{ header }}</h1>
        </div>
        <div class="local-hero-graphic-wrapper hidden-mobile">
          <div class="local-hero-graphic-holder tree-planting">
            <TreePlantingSvg :color="organizationDetails.darkBrandColor" />
          </div>
        </div>
      </div>
    </div>

    <div class="form-container donation-request-summary-form">
      <!-- Donation details -->
      <div class="donation-summary">
        <!-- Payment amount -->
        <div class="summary-item">
          <div class="field-name" v-translate>Amount</div>
          <div class="field-value">{{ donation.amount | currency }}</div>
        </div>

        <!-- Payment method -->
        <div v-if="donation.paymentLast4" class="summary-item">
          <div class="field-name" v-translate>Payment method</div>
          <div class="field-value">
            {{ $gettext(donation.paymentType) }} {{ donation.paymentLast4 }}
          </div>
        </div>

        <!-- Payment frequency -->
        <div class="summary-item">
          <div class="field-name" v-translate>Frequency</div>
          <div class="field-value">{{ formattedFrequency }}</div>
        </div>

        <!-- Recurring summary -->
        <div class="summary-item" v-if="isRecurringSummary">
          <div class="field-name" v-translate>Status</div>
          <div class="field-value">
            {{ donation.isSubscriptionActive ? $gettext('Active') : $gettext('Canceled') }}
          </div>
        </div>

        <!-- Payment date(s) -->
        <div class="summary-item" v-if="isRecurringSummary">
          <div class="field-name" v-translate>Donation Dates</div>
          <div class="field-value" v-for="d in donationsBySubscription.list" :key="d.id">
            {{ d.datetimeCreated | formatDateShort($language.current) }}
          </div>
        </div>
        <div class="summary-item" v-else>
          <div class="field-name" v-translate>Date</div>
          <div class="field-value">
            {{ this.donation.datetimeCreated | formatDateShort($language.current) }}
          </div>
        </div>
      </div>

      <!-- Donor fields-->
      <!-- First name -->
      <div v-if="anonymousDonor.firstName" class="form-field">
        <div class="field-name" v-translate>First name</div>
        <div class="field-value">{{ anonymousDonor.firstName }}</div>
      </div>

      <!-- Last name -->
      <div v-if="anonymousDonor.lastName" class="form-field">
        <div class="field-name" v-translate>Last name</div>
        <div class="field-value">{{ anonymousDonor.lastName }}</div>
      </div>

      <!-- Email -->
      <div v-if="anonymousDonor.email" class="form-field">
        <div class="field-name" v-translate>Email</div>
        <div class="field-value">{{ anonymousDonor.email }}</div>
      </div>

      <!-- Relationship to organization -->
      <div v-if="anonymousDonor.orgRelationship" class="form-field">
        <div
          class="field-name"
          v-translate="{orgName: organizationDetails.name}"
        >
          Your relationship to %{orgName}
        </div>
        <div class="field-value">{{ formattedOrgRelationship }}</div>
      </div>

      <!-- Unique ID -->
      <div v-if="anonymousDonor.employeeId" class="form-field">
        <div class="field-name" v-translate>Unique ID</div>
        <p class="summary-item-value">{{ anonymousDonor.employeeId }}</p>
      </div>

      <!-- Other relationship -->
      <div v-if="anonymousDonor.otherRelationship" class="form-field">
        <div class="field-name" v-translate>Details</div>
        <p class="summary-item-value">{{ anonymousDonor.otherRelationship }}</p>
      </div>

      <!-- Zip code -->
      <div v-if="anonymousDonor.zipCode" class="form-field">
        <div class="field-name" v-translate>Zip code</div>
        <p class="summary-item-value">{{ anonymousDonor.zipCode }}</p>
      </div>

      <!-- FAQ CTA -->
      <div class="form-footer">
        <span v-translate>For information on the tax status of donations,</span>&nbsp;
        <a :href="taxLink" target="_blank" v-translate>see the FAQ</a>.
      </div>

      <md-button v-if="donation.subscriptionId && donation.isSubscriptionActive">
        <p class="back-button" @click="onClickCancel" v-translate>Cancel my recurring donation</p>
      </md-button>
      <!--<LoadingSpinnerButton class="button" :text="this.home()" @click="onClickHome" />-->
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'

import AnonymousDonor from '@/services/anonymousDonors'
import Donation from '@/services/donations'
import CollectionManager from '@/services/collectionManager'
import FAQ from '@/services/faqs'
import TreePlantingSvg from '@/views/svg/TreePlantingSvg'
import LoadingSpinnerButton from '@/components/LoadingSpinnerButton'

export default {
  name: 'DonationSummary',
  components: {
    LoadingSpinnerButton,
    TreePlantingSvg,
  },
  data() {
    return {
      loading: true,
      donation: Donation.create({}),

      // Anonymous donor info
      anonymousDonorId: null,
      magicToken: null,
      anonymousDonor: AnonymousDonor.create({}),
      donationsBySubscription: CollectionManager.create({
        ModelClass: Donation,
      }),
      isRecurringSummary: false,
      language: this.user ? this.user.preferredLanguage : this.$language.current,
      taxLink: null,
    }
  },
  async created() {
    // First, handle any anonymous donor auth credentials passed in

    // Django template urlencodes query params
    this.magicToken = this.$route.query['amp;magicToken']
      ? this.$route.query['amp;magicToken']
      : this.$route.query.magicToken

    if (this.$route.query.donorId && this.magicToken) {
      await this.retrieveAnonymousDonor()
    }

    try {
      if (this.$route.params.subscriptionId) {
        this.isRecurringSummary = true
        await this.listDonationsBySubscription(this.$route.params.subscriptionId)

        // Next, populate this screen with a donation.
        // If we've received a Donation ID as a route param, use that
      } else if (this.$route.params.id) {
        await this.populateFromDonation(this.$route.params.id)
        // Otherwise, use whatever current donation exists in state
      } else if (this.currentDonation) {
        await this.populateFromState()
        // Finally, if this page is inappropriately accessed without a donation, route home
      } else {
        this.$router.push({ name: 'Home' })
        return
      }
    } catch (error) {
      console.log(error)
      this.$router.replace({
        name: 'Login',
        query: { next: this.$route.path },
      })
    }
    FAQ.api.getTcOrTaxFaq(this.organizationDetails.name, 'tax_q').then(faq => {
      this.taxLink = `/faq#${faq.faqOrder}`
    })
    this.loading = false
  },
  methods: {
    home() {
      return this.$gettext('Home')
    },
    async listDonationsBySubscription(subscriptionId) {
      this.donationsBySubscription.filters = { subscriptionId }
      if (this.anonymousDonorId) {
        this.donationsBySubscription.filters.magicToken = this.magicToken
        this.donationsBySubscription.filters.donorId = this.anonymousDonorId
      }
      await this.donationsBySubscription.refresh()
      this.donation = this.donationsBySubscription.list[0]
    },
    async retrieveAnonymousDonor() {
      this.anonymousDonorId = this.$route.query.donorId
      const anonymousDonor = await AnonymousDonor.api.retrieve(
        this.anonymousDonorId,
        this.anonymousDonorParams,
      )
      this.anonymousDonor = anonymousDonor
    },
    /* Populate this page with info from a donation in our database. */
    async populateFromDonation(donationId) {
      let result
      if (this.userIsLoggedIn) {
        result = await Donation.api.retrieve(donationId)
      } else {
        result = await Donation.api.retrieve(donationId, this.anonymousDonorParams)
      }
      if (result.frequency === 'RECURRING' && result.subscriptionId) {
        this.isRecurringSummary = true
        await this.listDonationsBySubscription(result.subscriptionId)
      } else {
        this.donation = result
      }
    },
    /**
     * Populate this page from a donation in state (which was just submitted and
     * may not exist in our database yet).
     */
    populateFromState() {
      this.donation = this.currentDonation

      // Once we're finished, clear out donation from state, since it's just been processed
      this.$store.dispatch('clearCurrentDonation')
    },
    onClickHome() {
      this.$router.push({ name: 'Home' })
    },
    async onClickCancel() {
      let confirmation = confirm(
        this.$gettext('Are you sure you want to cancel your recurring donation?'),
      )
      if (!confirmation) {
        return
      }
      if (this.userIsLoggedIn) {
        const employee = await Donation.api.cancelRecurringDonations(this.donation.subscriptionId)
        // Update current user to update "isMonthlyDonor" flag
        this.$store.dispatch('updateUser', employee)
        this.$Alert.alert({
          type: 'success',
          message: '<p>' + this.$gettext('Your recurring donation has been canceled.') + '</p>',
          timeout: 3000,
        })
        this.$router.push({ name: 'Home' })
      } else {
        this.anonymousDonor = await AnonymousDonor.api.cancelRecurringDonations(
          this.anonymousDonorParams,
          this.donation.subscriptionId,
        )
        this.$Alert.alert({
          type: 'success',
          message: '<p>' + this.$gettext('Your recurring donation has been canceled.') + '</p>',
          timeout: 3000,
        })
        this.$router.push({ name: 'Home' })
      }
    },
  },
  computed: {
    ...mapState(['user', 'organizationDetails', 'currentDonation']),
    ...mapGetters(['userIsLoggedIn']),
    formattedFrequency() {
      let frequency = Donation.options.FREQUENCY_CHOICES.find(
        choice => choice.value === this.donation.frequency,
      ).label

      if (
        this.donation.frequency === Donation.options.RECURRING &&
        !this.donation.isSubscriptionActive
      ) {
        frequency += this.$gettext(' (recurring payments canceled)')
      }

      return frequency
    },
    formattedOrgRelationship() {
      const label = AnonymousDonor.options.ORG_RELATIONSHIP_CHOICES.find(
        choice => choice.value === this.anonymousDonor.orgRelationship,
      ).label
      // Use organization custom variable if it exists
      if (this.anonymousDonor.orgRelationship === AnonymousDonor.options.EMPLOYEE) {
        return this.organizationDetails.customVariables.cv3 || label
      } else {
        return label
      }
    },
    /**
     * Return true if the user (authenticated or anonymous) who made this donation is
     * currently donating monthly.
     **/
    isMonthlyDonor() {
      if (this.userIsLoggedIn) {
        return this.user.isMonthlyDonor
      } else {
        return this.anonymousDonor.isMonthlyDonor
      }
    },
    /* Format anonymous donor authentication info to be sent as params to API */
    anonymousDonorParams() {
      return { donorId: this.anonymousDonorId, magicToken: this.magicToken }
    },
    header() {
      const translated = [
        this.$gettext('Thank you for supporting the'),
          this.$options.filters.translateToCurrentLang(this.$language.current, '!', ` ${this.organizationDetails.programName}!`),
      ]
      return `${translated[0]} ${this.organizationDetails.name}${translated[1]}`
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables';
@import '@/styles/mixins/buttons';
@import '@/styles/mixins/containers';
@import '@/styles/mixins/responsive';

.donation-request-summary {
  display: flex;
  flex-direction: column;

  .donation-request-summary-form {
    @extend .constrain-tablet-and-up;
  }
}

.tree-planting {
  align-items: end !important;
}

.donation-summary {
  display: flex;

  @include mobile-screen {
    margin-left: 5rem;
    margin-right: 5rem;
    flex-direction: column;
  }

  @include tablet-and-up-screen {
    flex-direction: row;
  }

  @include tablet-screen {
    flex-wrap: wrap;
  }
}

.summary-item {
  background-color: rgba(0, 42, 139, 0.1);
  border-radius: 12px;
  margin-bottom: 1.5rem;
  padding: 1.5rem;

  @include tablet-screen {
    width: 48%;
    margin-left: 0.5rem;
    margin-right: 0.5rem;

    &:nth-child(1) {
      margin-left: 0;
    }

    &:nth-child(2) {
      margin-right: 0;
    }

    &:nth-child(3) {
      margin-left: 0;
    }

    &:nth-child(4) {
      margin-right: 0;
    }
  }

  @include desktop-screen {
    width: 25%;
    margin-left: 0.5rem;
    margin-right: 0.5rem;

    &:nth-child(1) {
      margin-left: 0;
    }

    &:nth-child(4) {
      margin-right: 0;
    }
  }
}
</style>
